import {
  airlineDisplayName,
  syncableAirlineLogo,
  activeAssignedTrip,
  buildOpsStripRow,
  deriveFlightPhase,
  formatCancelRemark,
  formatDiversionLine,
  formatEte,
  formatHalifaxDateTime,
  acceptedQuoteRevenueCad,
  flightOpsStatusFromMission,
  FLIGHT_OPS_STATUS_LABEL,
  pickCurrentFlightTicket,
  pickCurrentTicket,
  sortOpsBoard,
  hasUsableFix,
  hydrateCrewPresenceList,
  hydrateFusedPosition,
  deskMissionsForRole,
  openAssignments,
  pickLiveTrack,
  plottableDestinations,
  contentXmlInventoryForCrew,
  mergeFlightTracks,
  hydrateFlightTrack,
  presenceForCrew,
  readLiveFleet,
  resolveCoords,
  routeLabel,
  starterScenery,
  ticketLifecycle,
  ticketStaMs,
  ticketStatusPillClass,
  ticketStatusTone,
  TICKET_LIFECYCLE_LABEL,
  type AircraftSpec,
  type AssignmentStatus,
  type CharterRequest,
  type CancelReasonCode,
  type CompanyProfile,
  type FlightTrack,
  type CrewMember,
  type DutyRoster,
  type CrewPresence,
  type FlightPhaseId,
  type FusedPosition,
  type FlightOpsStatus,
  hydrateMapboxSettings,
  type MapboxSettings,
  type Mission,
  type MissionStatus,
  type OpsStripRow,
  type PilotProfile,
  type SceneryAirport,
  type SyncSnapshot,
  type TicketLifecycle,
  type TicketStatusTone,
  type TripAssignment,
} from "@northstar/dispatch-core";
import { COMPANION_CREW_KEY } from "./companion";
import {
  companionSchedulePolicy,
  EMPTY_SCHEDULE_POLICY,
  type CompanionSchedulePolicy,
} from "./contentXmlPolicy";
import { companionGanttSource, EMPTY_GANTT_SOURCE, type CompanionGanttSource } from "./phoneGantt";

export type CompanionRosterRow = {
  id: string;
  displayName: string;
  roles: string[];
  isAdmin: boolean;
};

export type CompanionTrip = {
  id: string;
  callsign: string;
  route: string;
  originIcao: string;
  destIcao: string;
  status: MissionStatus;
  lifecycle: TicketLifecycle;
  lifecycleLabel: string;
  statusTone: TicketStatusTone;
  statusPillClass: string;
  opsStatus: FlightOpsStatus;
  opsStatusLabel: string;
  assignmentStatus: AssignmentStatus | null;
  departureIso: string;
  departureLabel: string;
  arrivalIso: string;
  arrivalLabel: string;
  eteMin: number;
  eteLabel: string;
  paxCount: number;
  cargoLb: number;
  loadLabel: string;
  flightType: string;
  divertToIcao: string;
  divertReason: string;
  divertLine: string;
  cancelReason: CancelReasonCode | "";
  cancelReasonNote: string;
  cancelRemark: string;
  registration: string;
  aircraftName: string;
  crewName: string;
  originMetar: string;
  destMetar: string;
  weatherNote: string;
  /** Accepted inbox quote for this mission. Absent when the desk has no figure. */
  revenueCad?: number;
};

export type CompanionLiveGlance = {
  hasFix: boolean;
  label: string;
  lat: number | null;
  lon: number | null;
  altitudeFt: number | null;
  groundSpeedKt: number | null;
  headingDeg: number | null;
  atUtc: string | null;
  stale: boolean;
  sourceLabel: string | null;
};

export type CompanionMapAirport = {
  icao: string;
  lat: number;
  lon: number;
};

/** Installed scenery / destination dot. Same set the desk map plots for this crew. */
export type CompanionMapField = {
  icao: string;
  name: string;
  lat: number;
  lon: number;
};

export type CompanionMapAircraft = {
  lat: number;
  lon: number;
  headingDeg: number | null;
  stale: boolean;
  label: string;
  registration: string;
  altitudeFt: number | null;
  groundSpeedKt: number | null;
  phaseLabel: string;
  atUtc: string | null;
};

export type CompanionFlightMap = {
  homeIcao: string;
  home: { lat: number; lon: number };
  origin: CompanionMapAirport | null;
  dest: CompanionMapAirport | null;
  divert: CompanionMapAirport | null;
  aircraft: CompanionMapAircraft | null;
  track: Array<{ lat: number; lon: number; altFt?: number }>;
  /** Installed scenery dots, excluding the filed dep / dest / divert pins. */
  fields: CompanionMapField[];
  /** Stored OFP lat/lon for the active ticket. Airway text is not geometry. */
  ofp: {
    originIcao?: string;
    destIcao?: string;
    routePoints?: Array<{ lat: number; lon: number }>;
    waypoints?: Array<{ ident?: string; lat?: number; lon?: number }>;
  } | null;
  mapbox: MapboxSettings;
};

export type CompanionCrewSnapshot = {
  companyId: string;
  companyName: string;
  /** Pack ref or small data URL. Empty when the desk kept the file local or has no logo. */
  companyLogo: string;
  homeIcao: string;
  pulledAtUtc: string;
  crewId: string | null;
  crewName: string | null;
  roster: CompanionRosterRow[];
  /** Focused crew has Dispatcher on the company roster. Pilot stays assigned-only. */
  canDispatchWrite: boolean;
  /**
   * `unmatched-company` is the login fallback: no roster row matched, so the
   * phone shows the company board instead of an empty schedule. More explains it.
   */
  boardScope: "company" | "assigned" | "unmatched-company";
  /** Trips on the company board before the crew filter. */
  companyTripCount: number;
  nextTrip: CompanionTrip | null;
  trips: CompanionTrip[];
  live: CompanionLiveGlance;
  ops: OpsStripRow;
  map: CompanionFlightMap;
  /** Synced Dispatcher Setting — refuse dest/origin writes outside the scan when on. */
  schedulePolicy: CompanionSchedulePolicy;
  /** Same missions as `trips`. Desk Gantt prefs (Local/Z, lanes) when the snapshot has them. */
  gantt: CompanionGanttSource;
};

export type CompanionSnapshotSource = {
  company?: {
    id?: string;
    operatorName?: string;
    name?: string;
    homeIcao?: string;
    scheduleOnlyContentXmlAirports?: boolean;
    allowPilotsCompanySchedule?: boolean;
    activeFlightId?: string;
    activeFlightAuthority?: "desk" | "megan" | "sim";
    ganttTimeDisplay?: CompanyProfile["ganttTimeDisplay"];
    enforceDutyClock?: boolean;
    deskBehavior?: CompanyProfile["deskBehavior"];
    defaultFuelUnit?: "lb" | "kg" | "gal" | "L";
  } | null;
  career?: {
    airlineName?: string;
    airlineLogo?: string;
    airlineLogoKeptLocal?: boolean;
  } | null;
  crew?: CrewMember[] | null;
  duty?: DutyRoster | null;
  missions?: Mission[] | null;
  assignments?: TripAssignment[] | null;
  lastKnownPosition?: FusedPosition | null;
  crewPresence?: CrewPresence[] | null;
  scenery?: SceneryAirport[] | null;
  fleet?: AircraftSpec[] | null;
  flightTracks?: FlightTrack[] | null;
  profile?: PilotProfile | null;
  mapboxSettings?: MapboxSettings | null;
  /** Published desk hero. Absent on an older snapshot. */
  heroStrip?: unknown;
  /** Desk RFQs. Revenue on a trip is that request's accepted quote when present. */
  inbox?: unknown;
  savedAtUtc?: string;
};

/** Port Hawkesbury — same default as the desk map when home ICAO has no coords. */
export const CYP_HOME = { lat: 45.6567, lon: -61.3681 };

export const EMPTY_FLIGHT_MAP: CompanionFlightMap = {
  homeIcao: "CYPD",
  home: CYP_HOME,
  origin: null,
  dest: null,
  divert: null,
  aircraft: null,
  track: [],
  fields: [],
  ofp: null,
  mapbox: hydrateMapboxSettings(null),
};

export const EMPTY_LIVE_GLANCE: CompanionLiveGlance = {
  hasFix: false,
  label: "No live position in this snapshot",
  lat: null,
  lon: null,
  altitudeFt: null,
  groundSpeedKt: null,
  headingDeg: null,
  atUtc: null,
  stale: false,
  sourceLabel: null,
};

export function loadCompanionCrewId(storage?: Pick<Storage, "getItem"> | null): string {
  return storage?.getItem(COMPANION_CREW_KEY)?.trim() ?? "";
}

export function saveCompanionCrewId(crewId: string, storage?: Pick<Storage, "setItem"> | null): string {
  const next = crewId.trim();
  storage?.setItem(COMPANION_CREW_KEY, next);
  return next;
}

/** Map the signed-in Nanode account onto this company's roster. Never guess another pilot. */
export function resolveAccountCrewId(input: {
  roster: readonly { id: string; displayName: string }[];
  accountEmail?: string;
  profileName?: string;
  profileEmail?: string;
}): string {
  const roster = input.roster;
  const name = input.profileName?.trim().toLowerCase() ?? "";
  if (name) {
    const hit = roster.find((row) => row.displayName.trim().toLowerCase() === name || row.id.toLowerCase() === name);
    if (hit) return hit.id;
  }
  const email = (input.profileEmail || input.accountEmail || "").trim().toLowerCase();
  const local = email.split("@")[0] ?? "";
  if (local) {
    const hit = roster.find(
      (row) => row.id.toLowerCase() === local || row.displayName.trim().toLowerCase() === local,
    );
    if (hit) return hit.id;
  }
  return roster.length === 1 ? roster[0].id : "";
}

/** Stable key for “this Nanode company” so leave/switch can drop a stale crew focus. */
export function companyMembershipKey(
  company?: { id?: string; operatorName?: string; name?: string } | null,
): string {
  if (!company) return "";
  const id = typeof company.id === "string" ? company.id.trim() : "";
  if (id) return id;
  return (company.operatorName?.trim() || company.name?.trim() || "").toLowerCase();
}

/**
 * After a board pull: prefer the signed-in account on this roster.
 * If the company changed, never keep the previous company’s crew id.
 */
export function focusCrewAfterCompanyPull(input: {
  roster: readonly { id: string; displayName: string }[];
  previousCrewId?: string;
  previousCompanyKey?: string;
  nextCompanyKey?: string;
  accountEmail?: string;
  profileName?: string;
  profileEmail?: string;
}): string {
  const resolved = resolveAccountCrewId({
    roster: input.roster,
    accountEmail: input.accountEmail,
    profileName: input.profileName,
    profileEmail: input.profileEmail,
  });
  if (resolved) return resolved;
  const prevCompany = input.previousCompanyKey?.trim() ?? "";
  const nextCompany = input.nextCompanyKey?.trim() ?? "";
  if (prevCompany && nextCompany && prevCompany !== nextCompany) return "";
  const prevCrew = input.previousCrewId?.trim() ?? "";
  if (prevCrew && input.roster.some((row) => row.id === prevCrew)) return prevCrew;
  return "";
}

export function mergeLiveIntoSnapshot(
  snapshot: SyncSnapshot,
  live: { crewPresence?: unknown; lastKnownPosition?: unknown; exportedAtUtc?: string; flightTrack?: unknown },
): SyncSnapshot {
  const incoming = hydrateFlightTrack(
    live.flightTrack && typeof live.flightTrack === "object" ? (live.flightTrack as Partial<FlightTrack>) : null,
  );
  const flightTracks = incoming
    ? mergeFlightTracks(snapshot.payload.flightTracks, [incoming])
    : snapshot.payload.flightTracks;
  return {
    ...snapshot,
    exportedAtUtc: live.exportedAtUtc || snapshot.exportedAtUtc,
    payload: {
      ...snapshot.payload,
      crewPresence: Array.isArray(live.crewPresence)
        ? hydrateCrewPresenceList(live.crewPresence)
        : snapshot.payload.crewPresence,
      lastKnownPosition:
        live.lastKnownPosition !== undefined
          ? hydrateFusedPosition(live.lastKnownPosition as Partial<FusedPosition> | null)
          : snapshot.payload.lastKnownPosition,
      flightTracks,
    },
  };
}

function rosterFrom(crew: CrewMember[] | null | undefined): CompanionRosterRow[] {
  return (crew ?? []).map((row) => ({
    id: row.id,
    displayName: row.displayName,
    roles: row.roles,
    isAdmin: Boolean(row.isAdmin),
  }));
}

/** Same gate as Nanode POST /v1/board/ticket — Dispatcher role, not Pilot. */
export function companionCanDispatchWrite(row?: Pick<CompanionRosterRow, "roles"> | null): boolean {
  if (!row) return false;
  return (row.roles ?? []).some((role) => String(role).trim().toLowerCase() === "dispatcher");
}

function formatClock(iso: string, empty: string): string {
  if (!iso) return empty;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return empty;
  return formatHalifaxDateTime(date);
}

function formatLoad(paxCount: number, cargoLb: number): string {
  const pax = Number.isFinite(paxCount) ? Math.max(0, Math.round(paxCount)) : 0;
  const cargo = Number.isFinite(cargoLb) ? Math.max(0, Math.round(cargoLb)) : 0;
  return `${pax} pax · ${cargo} lb`;
}

function postedCharterRevenue(raw: unknown, missionId: string): number | null {
  if (!raw || typeof raw !== "object") return null;
  const transactions = (raw as { transactions?: unknown }).transactions;
  if (!Array.isArray(transactions)) return null;
  for (const row of transactions) {
    if (!row || typeof row !== "object") continue;
    const txn = row as { missionId?: unknown; kind?: unknown; amountCad?: unknown };
    if (txn.missionId !== missionId || txn.kind !== "charter_revenue") continue;
    if (typeof txn.amountCad !== "number" || !Number.isFinite(txn.amountCad) || txn.amountCad <= 0) continue;
    return Math.round(txn.amountCad * 100) / 100;
  }
  return null;
}

function quoteInbox(
  raw: unknown,
): Pick<CharterRequest, "id" | "acceptedMissionId" | "deskQuote">[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  return raw.filter(
    (row): row is Pick<CharterRequest, "id" | "acceptedMissionId" | "deskQuote"> =>
      Boolean(row) && typeof row === "object",
  );
}

function assignmentFor(assignments: TripAssignment[], missionId: string): TripAssignment | undefined {
  return openAssignments(assignments).find((a) => a.missionId === missionId)
    ?? assignments.find((a) => !a.superseded && a.missionId === missionId && a.status !== "declined");
}

export function tripFromMission(
  mission: Mission,
  assignment?: TripAssignment | null,
  opts?: { now?: Date; revenueCad?: number },
): CompanionTrip {
  const departureIso = mission.departureIso || mission.createdAt || "";
  const opsStatus = flightOpsStatusFromMission(mission, { now: opts?.now });
  const lifecycle = ticketLifecycle(mission, assignment);
  const staMs = ticketStaMs(mission);
  const arrivalIso =
    Number.isFinite(staMs) && staMs != null ? new Date(staMs).toISOString() : "";
  const paxCount = Number.isFinite(mission.paxCount) ? Number(mission.paxCount) : 0;
  const cargoLb = Number.isFinite(mission.cargoLb) ? Number(mission.cargoLb) : 0;
  const cancelReason = mission.cancelReason ?? "";
  const cancelReasonNote = mission.cancelReasonNote ?? "";
  return {
    id: mission.id,
    callsign: mission.callsign,
    route: routeLabel(mission),
    originIcao: mission.originIcao,
    destIcao: mission.destIcao,
    status: mission.status,
    lifecycle,
    lifecycleLabel: TICKET_LIFECYCLE_LABEL[lifecycle],
    statusTone: ticketStatusTone(lifecycle),
    statusPillClass: ticketStatusPillClass(lifecycle),
    opsStatus,
    opsStatusLabel: FLIGHT_OPS_STATUS_LABEL[opsStatus],
    assignmentStatus: assignment?.status ?? null,
    departureIso,
    departureLabel: departureIso ? `STD ${formatClock(departureIso, "—")}` : "STD —",
    arrivalIso,
    arrivalLabel: arrivalIso ? `STA ${formatClock(arrivalIso, "—")}` : "STA —",
    eteMin: mission.eteMin,
    eteLabel: formatEte(mission.eteMin),
    paxCount,
    cargoLb,
    loadLabel: formatLoad(paxCount, cargoLb),
    flightType: mission.flightType ?? "",
    divertToIcao: mission.divertToIcao ?? "",
    divertReason: mission.divertReason ?? "",
    divertLine: formatDiversionLine(mission),
    cancelReason,
    cancelReasonNote,
    cancelRemark: cancelReason ? formatCancelRemark(cancelReason, cancelReasonNote || undefined) : "",
    registration: mission.registration,
    aircraftName: mission.aircraftName,
    crewName: assignment?.crewName ?? "",
    originMetar: mission.originMetar ?? "",
    destMetar: mission.destMetar ?? "",
    weatherNote: mission.weatherNote ?? "",
    ...(typeof opts?.revenueCad === "number" && Number.isFinite(opts.revenueCad) ? { revenueCad: opts.revenueCad } : {}),
  };
}

function sortTrips(trips: CompanionTrip[], missions: Mission[]): CompanionTrip[] {
  const order = new Map(sortOpsBoard(missions).map((m, i) => [m.id, i]));
  return [...trips].sort((a, b) => (order.get(a.id) ?? 999) - (order.get(b.id) ?? 999));
}

function missionsForCompanion(
  missions: Mission[],
  assignments: TripAssignment[],
  crewId: string,
  opts?: { allowCompanySchedule?: boolean; scheduleView?: string | null },
): Mission[] {
  if (!crewId) return [];
  return deskMissionsForRole(missions, assignments, {
    role: "pilot",
    crewId,
    allowCompanySchedule: opts?.allowCompanySchedule,
    scheduleView: opts?.scheduleView,
  });
}

function pickFix(
  crewId: string,
  source: CompanionSnapshotSource,
): { fused: FusedPosition; sourceLabel: string } | null {
  const presence = presenceForCrew(source.crewPresence ?? [], crewId);
  if (presence && hasUsableFix(presence.fused)) {
    return { fused: presence.fused, sourceLabel: "Published crew fix" };
  }
  const lastKnown = source.lastKnownPosition;
  if (hasUsableFix(lastKnown)) {
    const simLink = lastKnown.source === "simconnect" || lastKnown.source === "sayintentions";
    const rosterCount = (source.crew ?? []).length;
    if (simLink || (crewId && rosterCount <= 1)) {
      return {
        fused: lastKnown,
        sourceLabel: simLink ? "Published SimLink fix" : "Last known position",
      };
    }
  }
  return null;
}

export function liveGlanceFromSnapshot(source: CompanionSnapshotSource, crewId = ""): CompanionLiveGlance {
  const picked = pickFix(crewId, source);
  if (!picked) return { ...EMPTY_LIVE_GLANCE };
  const { fused, sourceLabel } = picked;
  const groundSpeedKt = fused.groundSpeed ?? fused.groundSpeedKt ?? null;
  return {
    hasFix: true,
    label: fused.stale ? "Stale position in snapshot" : "Position in snapshot",
    lat: fused.lat,
    lon: fused.lon,
    altitudeFt: fused.altitudeFt ?? null,
    groundSpeedKt: Number.isFinite(groundSpeedKt) ? Number(groundSpeedKt) : null,
    headingDeg: fused.heading ?? fused.trackDeg ?? null,
    atUtc: fused.atUtc || null,
    stale: Boolean(fused.stale),
    sourceLabel,
  };
}

/** Phone copy — desk SimConnect wait notes talk about “this PC.” */
export function companionPhaseWaitNote(phase: { fromSim: boolean; note: string }, hasFix: boolean): string {
  if (phase.fromSim) return phase.note;
  if (hasFix) {
    return "Position is published, but phase needs a live SimConnect sample from the desk. This phone does not invent taxi or cruise.";
  }
  return "Waiting for a live SimConnect sample from the desk. This phone does not invent taxi or cruise.";
}

export function idleOpsRow(now?: Date): OpsStripRow {
  return buildOpsStripRow({
    now: now ?? new Date("2026-09-15T16:00:00.000Z"),
    phase: deriveFlightPhase({ fused: null }),
  });
}

function sceneryFor(source: CompanionSnapshotSource): SceneryAirport[] {
  return source.scenery?.length ? source.scenery : starterScenery();
}

/** Same plottable set as the desk map for this crew’s Content.xml scan plus company destinations. */
export function companionMapFields(
  source: CompanionSnapshotSource,
  input: { crewId?: string; homeIcao?: string; mission?: Mission | null } = {},
): CompanionMapField[] {
  const inventory = contentXmlInventoryForCrew(source.crew, input.crewId);
  const plotted = plottableDestinations(source.scenery ?? [], inventory, {
    preferIcaos: [input.homeIcao, input.mission?.originIcao, input.mission?.destIcao, input.mission?.divertToIcao].filter(
      (code): code is string => Boolean(code?.trim()),
    ),
  }).plotted;
  const pins = new Set(
    [input.mission?.originIcao, input.mission?.destIcao, input.mission?.divertToIcao]
      .map((code) => (code ?? "").trim().toUpperCase())
      .filter(Boolean),
  );
  const fields: CompanionMapField[] = [];
  for (const apt of plotted) {
    const icao = apt.icao.trim().toUpperCase();
    if (!icao || pins.has(icao)) continue;
    if (typeof apt.lat !== "number" || typeof apt.lon !== "number") continue;
    if (!Number.isFinite(apt.lat) || !Number.isFinite(apt.lon)) continue;
    fields.push({ icao, name: apt.name?.trim() || icao, lat: apt.lat, lon: apt.lon });
  }
  return fields;
}

function mapAirport(icao: string, scenery: SceneryAirport[]): CompanionMapAirport | null {
  const code = icao.trim().toUpperCase();
  if (!code) return null;
  const coords = resolveCoords(code, scenery);
  return coords ? { icao: code, ...coords } : null;
}

export function opsRowFromSnapshot(
  source: CompanionSnapshotSource,
  input: {
    crewId?: string;
    mission?: Mission | null;
    previousPhase?: FlightPhaseId | null;
    now?: Date;
    callsign?: string | null;
  } = {},
): OpsStripRow {
  const crewId = input.crewId?.trim() ?? "";
  const picked = pickFix(crewId, source);
  const fused = picked?.fused ?? null;
  const now = input.now ?? new Date();
  const phase = deriveFlightPhase({
    fused,
    previous: input.previousPhase,
    now,
    simconnectEnabled: fused?.source === "simconnect",
  });
  return buildOpsStripRow({
    crew: (source.crew ?? []).find((row) => row.id === crewId) ?? null,
    profile: source.profile ?? null,
    mission: input.mission ?? null,
    fused,
    fleet: readLiveFleet(source.fleet),
    scenery: sceneryFor(source),
    missions: input.mission ? [input.mission] : [],
    now,
    callsign: input.callsign,
    phase: {
      ...phase,
      note: companionPhaseWaitNote(phase, Boolean(picked)),
    },
  });
}

export function flightMapFromSnapshot(
  source: CompanionSnapshotSource,
  input: {
    crewId?: string;
    mission?: Mission | null;
    live?: CompanionLiveGlance;
    ops?: OpsStripRow;
  } = {},
): CompanionFlightMap {
  const scenery = sceneryFor(source);
  const homeIcao = source.company?.homeIcao?.trim().toUpperCase() || "CYPD";
  const home = resolveCoords(homeIcao, scenery) ?? CYP_HOME;
  const live = input.live ?? liveGlanceFromSnapshot(source, input.crewId ?? "");
  const ops = input.ops;
  const origin = mapAirport(input.mission?.originIcao ?? "", scenery);
  const dest = mapAirport(input.mission?.destIcao ?? "", scenery);
  const divertIcao = (input.mission?.divertToIcao ?? "").trim().toUpperCase();
  const divert =
    divertIcao && divertIcao !== (input.mission?.destIcao ?? "").trim().toUpperCase()
      ? mapAirport(divertIcao, scenery)
      : null;
  const track = pickLiveTrack(source.flightTracks, input.mission ?? null);
  const aircraft =
    live.hasFix && live.lat != null && live.lon != null && Number.isFinite(live.lat) && Number.isFinite(live.lon)
      ? {
          lat: live.lat,
          lon: live.lon,
          headingDeg: live.headingDeg,
          stale: live.stale,
          label: ops?.callsign || ops?.registration || ops?.aircraftType || "ACFT",
          registration: ops?.registration || "",
          altitudeFt: live.altitudeFt,
          groundSpeedKt: live.groundSpeedKt,
          phaseLabel: ops?.phase?.label ?? "",
          atUtc: live.atUtc,
        }
      : null;
  return {
    homeIcao,
    home,
    origin,
    dest,
    divert,
    aircraft,
    track: (track?.points ?? [])
      .filter((point) => Number.isFinite(point.lat) && Number.isFinite(point.lon))
      .map((point) => ({
        lat: point.lat,
        lon: point.lon,
        ...(typeof point.altFt === "number" && Number.isFinite(point.altFt) ? { altFt: point.altFt } : {}),
      })),
    fields: companionMapFields(source, { crewId: input.crewId, homeIcao, mission: input.mission }),
    ofp: mapOfp(input.mission),
    mapbox: hydrateMapboxSettings(source.mapboxSettings),
  };
}

function finiteCoord(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim()) {
    const n = Number(value);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

function mapOfp(mission?: Mission | null): CompanionFlightMap["ofp"] {
  const attachment = mission?.ofpAttachment;
  if (!attachment) return null;
  const routePoints = (attachment.routePoints ?? []).flatMap((point) => {
    const lat = finiteCoord(point.lat);
    const lon = finiteCoord(point.lon);
    return lat == null || lon == null ? [] : [{ lat, lon }];
  });
  const waypoints = (attachment.waypoints ?? []).flatMap((fix) => {
    const lat = finiteCoord(fix.lat);
    const lon = finiteCoord(fix.lon);
    if (lat == null || lon == null) return [];
    const ident = fix.ident?.trim().toUpperCase();
    return [{ ...(ident ? { ident } : {}), lat, lon }];
  });
  if (!routePoints.length && !waypoints.length) return null;
  const originIcao = attachment.originIcao?.trim().toUpperCase();
  const destIcao = attachment.destIcao?.trim().toUpperCase();
  return {
    ...(originIcao ? { originIcao } : {}),
    ...(destIcao ? { destIcao } : {}),
    ...(routePoints.length ? { routePoints } : {}),
    ...(waypoints.length ? { waypoints } : {}),
  };
}

export function projectCrewSnapshot(
  source: CompanionSnapshotSource | SyncSnapshot | null | undefined,
  opts?: { crewId?: string | null; nowIso?: string; previousPhase?: FlightPhaseId | null; now?: Date },
): CompanionCrewSnapshot {
  const payload = source && "payload" in source ? source.payload : source;
  const savedAtUtc =
    source && "exportedAtUtc" in source && typeof source.exportedAtUtc === "string"
      ? source.exportedAtUtc
      : source && "savedAtUtc" in source && typeof source.savedAtUtc === "string"
        ? source.savedAtUtc
        : opts?.nowIso ?? "";
  const company = payload?.company;
  const companyId = company && "id" in company && typeof company.id === "string" ? company.id.trim() : "";
  const storedName =
    (company && "operatorName" in company ? company.operatorName?.trim() : "") ||
    (company && "name" in company ? company.name?.trim() : "") ||
    "";
  const career = payload && "career" in payload ? payload.career : undefined;
  const companyName = airlineDisplayName(career, storedName);
  const companyLogo = syncableAirlineLogo(
    career
      ? {
          airlineLogo: career.airlineLogo ?? "",
          airlineLogoKeptLocal: career.airlineLogoKeptLocal === true,
        }
      : null,
  );
  const homeIcao = payload?.company?.homeIcao?.trim() || "";
  const roster = rosterFrom(payload?.crew);
  const crewId = opts?.crewId?.trim() || "";
  const crewName = roster.find((row) => row.id === crewId)?.displayName ?? null;
  const missions = payload?.missions ?? [];
  const assignments = payload?.assignments ?? [];
  const canDispatchWrite = companionCanDispatchWrite(roster.find((row) => row.id === crewId) ?? null);
  const signed = (payload?.crew ?? []).find((row) => row.id === crewId);
  const unmatchedCompany = Boolean(payload) && !crewId && roster.length > 0 && missions.length > 0;
  const boardScope: CompanionCrewSnapshot["boardScope"] = canDispatchWrite
    ? "company"
    : unmatchedCompany
      ? "unmatched-company"
      : "assigned";
  const focused = canDispatchWrite || unmatchedCompany
    ? missions
    : missionsForCompanion(missions, assignments, crewId, {
        allowCompanySchedule: payload?.company?.allowPilotsCompanySchedule === true,
        scheduleView: signed?.pilotScheduleView,
      });
  const now = opts?.now ?? (savedAtUtc ? new Date(savedAtUtc) : undefined);
  const tripNow = now && !Number.isNaN(now.getTime()) ? now : undefined;
  const inbox = quoteInbox(payload && "inbox" in payload ? payload.inbox : undefined);
  const account = payload && "account" in payload ? payload.account : undefined;
  const tripOpts = (mission: Mission) => {
    const revenueCad = postedCharterRevenue(account, mission.id) ?? acceptedQuoteRevenueCad(mission, inbox);
    return { now: tripNow, ...(revenueCad != null ? { revenueCad } : {}) };
  };
  const trips = sortTrips(
    focused.map((mission) => tripFromMission(mission, assignmentFor(assignments, mission.id), tripOpts(mission))),
    focused,
  );
  const current =
    pickCurrentFlightTicket({
      missions: focused,
      assignments,
      crewId: crewId || undefined,
      fused: payload?.lastKnownPosition ?? null,
      scenery: payload?.scenery ?? undefined,
      activeFlightId: payload?.company?.activeFlightId,
      activeFlightAuthority: payload?.company?.activeFlightAuthority,
      fleet: readLiveFleet(payload?.fleet),
    }) ?? pickCurrentTicket({ missions: focused, assignments, crewId: crewId || undefined });
  const active = activeAssignedTrip(assignments, missions, crewId ? { crewId } : undefined);
  const nextMission = current ?? active?.mission ?? focused.find((m) => m.id === trips.find((t) => t.status === "pending" || t.status === "in-progress")?.id);
  const nextTrip = nextMission
    ? tripFromMission(nextMission, assignmentFor(assignments, nextMission.id) ?? active?.assignment, tripOpts(nextMission))
    : trips[0] ?? null;
  const live = liveGlanceFromSnapshot(payload ?? {}, crewId);
  const ops = opsRowFromSnapshot(payload ?? {}, {
    crewId,
    mission: nextMission ?? null,
    previousPhase: opts?.previousPhase,
    now: now && !Number.isNaN(now.getTime()) ? now : undefined,
    callsign: nextTrip?.callsign,
  });
  const map = flightMapFromSnapshot(payload ?? {}, {
    crewId,
    mission: nextMission ?? null,
    live,
    ops,
  });

  return {
    companyId,
    companyName,
    companyLogo,
    homeIcao,
    pulledAtUtc: savedAtUtc,
    crewId: crewId || null,
    crewName,
    roster,
    canDispatchWrite,
    boardScope,
    companyTripCount: missions.length,
    nextTrip,
    trips,
    live,
    ops,
    map,
    schedulePolicy: companionSchedulePolicy(payload ?? {}),
    gantt: companionGanttSource({
      missions: focused,
      fleet: readLiveFleet(payload?.fleet),
      assignments,
      crew: roster.map((row) => ({ id: row.id, name: row.displayName })),
      duty: payload?.duty,
      company,
      homeIcao,
      ticketOriginIcao: nextMission?.originIcao,
      now: tripNow,
    }),
  };
}

export function emptyCompanionTrip(partial: Partial<CompanionTrip> = {}): CompanionTrip {
  return {
    id: "m0",
    callsign: "CEL000",
    route: "CYPD–CYHZ",
    originIcao: "CYPD",
    destIcao: "CYHZ",
    status: "pending",
    lifecycle: "scheduled",
    lifecycleLabel: "Scheduled",
    statusTone: "scheduled",
    statusPillClass: "pill-life-scheduled",
    opsStatus: "on_time",
    opsStatusLabel: "On time",
    assignmentStatus: "accepted",
    departureIso: "2026-09-16T14:00:00.000Z",
    departureLabel: "STD —",
    arrivalIso: "",
    arrivalLabel: "STA —",
    eteMin: 35,
    eteLabel: "0:35",
    paxCount: 0,
    cargoLb: 0,
    loadLabel: "0 pax · 0 lb",
    flightType: "corporate",
    divertToIcao: "",
    divertReason: "",
    divertLine: "",
    cancelReason: "",
    cancelReasonNote: "",
    cancelRemark: "",
    registration: "C-GCEA",
    aircraftName: "Hawker 800XP",
    crewName: "Emerson",
    originMetar: "",
    destMetar: "",
    weatherNote: "",
    ...partial,
  };
}

export function emptyCompanionView(partial: Partial<CompanionCrewSnapshot> = {}): CompanionCrewSnapshot {
  return {
    companyId: "",
    companyName: "",
    companyLogo: "",
    homeIcao: "",
    pulledAtUtc: "",
    crewId: null,
    crewName: null,
    roster: [],
    canDispatchWrite: false,
    boardScope: "assigned",
    companyTripCount: 0,
    nextTrip: null,
    trips: [],
    live: EMPTY_LIVE_GLANCE,
    ops: idleOpsRow(),
    map: { ...EMPTY_FLIGHT_MAP },
    schedulePolicy: EMPTY_SCHEDULE_POLICY,
    gantt: EMPTY_GANTT_SOURCE,
    ...partial,
  };
}

export function formatCoord(value: number | null, kind: "lat" | "lon"): string {
  if (value === null || !Number.isFinite(value)) return "—";
  const abs = Math.abs(value).toFixed(3);
  if (kind === "lat") return `${abs}°${value >= 0 ? "N" : "S"}`;
  return `${abs}°${value >= 0 ? "E" : "W"}`;
}

export function formatAltitude(ft: number | null): string {
  if (ft === null || !Number.isFinite(ft)) return "—";
  return `${Math.round(ft).toLocaleString("en-CA")} ft`;
}

export function formatGroundspeed(kt: number | null): string {
  if (kt === null || !Number.isFinite(kt)) return "—";
  return `${Math.round(kt)} kt`;
}

export function formatHeading(deg: number | null): string {
  if (deg === null || !Number.isFinite(deg)) return "—";
  return `${String(((Math.round(deg) % 360) + 360) % 360).padStart(3, "0")}°`;
}
